import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { invalidateQuoteRequestLinesDebounced } from "#modules/quoteRequestLines/infra/invalidateQuoteRequestLinesDebounced";
import { setQuoteRequestLineQueryData } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";

import {
  bulkUpdateQuoteRequestLineQuantity,
  BulkUpdateQuoteRequestLineQuantityDto,
} from "../infra/bulkUpdateQuoteRequestLineQuantity.api";

export const useBulkUpdateQuoteRequestLineQuantity = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: (payload: BulkUpdateQuoteRequestLineQuantityDto) =>
      bulkUpdateQuoteRequestLineQuantity(payload),
    onSuccess: (result, { quantity }) => {
      result.updatedQuoteRequestLines.forEach(({ id }) => {
        setQuoteRequestLineQueryData(queryClient, id, (previous) =>
          previous ? { ...previous, quantity } : previous,
        );

        invalidateQuoteRequestLinesDebounced({
          queryClient,
          quoteRequestLineId: id,
        });
      });

      if (result.skippedQuoteRequestLineIds && result.skippedQuoteRequestLineIds.length > 0) {
        showErrorToast({
          color: "orange",
          title: t("quoteRequestLine.bulkActionSkippedAnalyzing", {
            count: result.skippedQuoteRequestLineIds.length,
          }),
        });
      }
    },
    onError: (err: Error) => {
      showErrorToast({ title: err.message });
      errorHandler(err);
    },
  });

  return { bulkUpdateQuoteRequestLineQuantity: mutate, ...rest };
};
